// Safe direct-rename execution planner (shared core, adapter-agnostic).
//
// Produces an ordered list of primitive move ops that a filesystem adapter
// executes via stable handles. Guarantees:
//  - deepest files/folders first, parent folders last (no path invalidation)
//  - swaps (A<->B), cycles (A->B->C->A) and case-only renames are performed
//    through unique temporary names so nothing is ever overwritten
//  - temporary names never remain after success

import { buildApplySteps } from "@/lib/rename/script";

const rnd = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2)).replace(/-/g, "");

export const tempName = (i) => `.rename-studio-temp-${rnd()}${i != null ? `-${i}` : ""}`;

// A group (all items sharing a parent) needs two-phase temp moves when a target
// name currently belongs to another moving sibling, or it's a case-only rename.
function groupNeedsTemp(group) {
  const sources = new Set(group.map((s) => s.oldBasename.toLowerCase()));
  return group.some((s) => {
    const t = s.newBasename.toLowerCase();
    const o = s.oldBasename.toLowerCase();
    if (t === o && s.newBasename !== s.oldBasename) return true; // case-only
    return t !== o && sources.has(t); // target held by another source
  });
}

// Returns { ops:[{handle,to,isTemp,id}], steps }  (steps = item-level plan)
export function planExecution(rows) {
  const steps = buildApplySteps(rows);
  if (!steps.length) return { ops: [], steps };

  const groups = new Map();
  for (const s of steps) {
    const k = s.parent || "";
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(s);
  }
  // deepest groups first
  const ordered = [...groups.values()].sort(
    (a, b) => Math.max(...b.map((s) => s.depth)) - Math.max(...a.map((s) => s.depth))
  );

  const ops = [];
  for (const group of ordered) {
    if (groupNeedsTemp(group)) {
      const temps = group.map((s, i) => ({ s, temp: tempName(i) }));
      temps.forEach(({ s, temp }) => ops.push({ handle: s.handle, to: temp, isTemp: true, id: s.id }));
      temps.forEach(({ s }, i) => ops.push({ handle: s.handle, to: s.newBasename, isTemp: false, id: s.id }));
    } else {
      group.forEach((s) => ops.push({ handle: s.handle, to: s.newBasename, isTemp: false, id: s.id }));
    }
  }
  return { ops, steps };
}

// Build reverse item-rows from a completed item-level plan (for Undo).
export function reverseRows(completed) {
  return completed.map((s) => ({
    id: s.id, include: true,
    original: s.newBasename, suggested: s.oldBasename,
    path: s.path, parent: s.parent, depth: s.depth, type: s.type, _handle: s.handle,
  }));
}
